// Pre-baked translations for the most common app strings.
// Hydrated into the localStorage cache at app boot so the very
// first time a user switches language, every common UI label,
// kicker, button, and short heading is instant — no Claude
// round-trip, no perceived "translating…" delay.
//
// Maintenance: when adding new <Tr> usage with frequently-seen
// English strings, add them here so first-paint stays snappy in
// every language. Keep the list focused on strings that appear
// above the fold or in navigation; long body text doesn't need
// a manifest entry because it's batched lazily anyway.
//
// Languages cover the LANGUAGES list in lib/constants.js and
// match what the picker displays. New languages can be added
// without code changes — they'll just translate on first use.

const MANIFEST = {
  // ── Navigation & global UI ──────────────────────────────────
  "Bible": {
    es: "Biblia", fr: "Bible", pt: "Bíblia", de: "Bibel", it: "Bibbia",
    ru: "Библия", hi: "बाइबल", ar: "الكتاب المقدس", "zh-CN": "圣经",
    sw: "Biblia", ko: "성경", vi: "Kinh Thánh", tl: "Bibliya",
    ht: "Bib la", ja: "聖書", nl: "Bijbel", pl: "Biblia", tr: "İncil",
  },
  "Reference": {
    es: "Referencia", fr: "Référence", pt: "Referência", de: "Referenz", it: "Riferimento",
    ru: "Справка", hi: "संदर्भ", ar: "مرجع", "zh-CN": "参考",
    sw: "Marejeo", ko: "참조", vi: "Tham khảo", tl: "Sanggunian",
    ht: "Referans", ja: "参照", nl: "Referentie", pl: "Odniesienie", tr: "Referans",
  },
  "Search": {
    es: "Buscar", fr: "Rechercher", pt: "Pesquisar", de: "Suchen", it: "Cerca",
    ru: "Поиск", hi: "खोजें", ar: "بحث", "zh-CN": "搜索",
    sw: "Tafuta", ko: "검색", vi: "Tìm kiếm", tl: "Maghanap",
    ht: "Chèche", ja: "検索", nl: "Zoeken", pl: "Szukaj", tr: "Ara",
  },
  "Chapter": {
    es: "Capítulo", fr: "Chapitre", pt: "Capítulo", de: "Kapitel", it: "Capitolo",
    ru: "Глава", hi: "अध्याय", ar: "فصل", "zh-CN": "章",
    sw: "Sura", ko: "장", vi: "Chương", tl: "Kabanata",
    ht: "Chapit", ja: "章", nl: "Hoofdstuk", pl: "Rozdział", tr: "Bölüm",
  },
  "ch": {
    es: "cap", fr: "ch", pt: "cap", de: "Kap", it: "cap",
    ru: "гл", hi: "अ", ar: "ف", "zh-CN": "章",
    sw: "sura", ko: "장", vi: "ch", tl: "kab",
    ht: "ch", ja: "章", nl: "hfst", pl: "rozdz", tr: "böl",
  },
  "References": {
    es: "Referencias", fr: "Références", pt: "Referências", de: "Referenzen", it: "Riferimenti",
    ru: "Ссылки", hi: "संदर्भ", ar: "مراجع", "zh-CN": "参考资料",
    sw: "Marejeo", ko: "참조", vi: "Tham chiếu", tl: "Mga Sanggunian",
    ht: "Referans", ja: "参照", nl: "Referenties", pl: "Odniesienia", tr: "Referanslar",
  },
  "Old": {
    es: "Antiguo", fr: "Ancien", pt: "Antigo", de: "Alt", it: "Antico",
    ru: "Ветхий", hi: "पुराना", ar: "قديم", "zh-CN": "旧约",
    sw: "Agano la Kale", ko: "구약", vi: "Cũ", tl: "Luma",
    ht: "Ansyen", ja: "旧約", nl: "Oude", pl: "Stary", tr: "Eski",
  },
  "New": {
    es: "Nuevo", fr: "Nouveau", pt: "Novo", de: "Neu", it: "Nuovo",
    ru: "Новый", hi: "नया", ar: "جديد", "zh-CN": "新约",
    sw: "Agano Jipya", ko: "신약", vi: "Mới", tl: "Bago",
    ht: "Nouvo", ja: "新約", nl: "Nieuwe", pl: "Nowy", tr: "Yeni",
  },
  "Loading…": {
    es: "Cargando…", fr: "Chargement…", pt: "Carregando…", de: "Lädt…", it: "Caricamento…",
    ru: "Загрузка…", hi: "लोड हो रहा है…", ar: "جار التحميل…", "zh-CN": "加载中…",
    sw: "Inapakia…", ko: "로딩 중…", vi: "Đang tải…", tl: "Naglo-load…",
    ht: "Ap chaje…", ja: "読み込み中…", nl: "Laden…", pl: "Ładowanie…", tr: "Yükleniyor…",
  },
  "translating": {
    es: "traduciendo", fr: "traduction", pt: "traduzindo", de: "übersetzen", it: "traduzione",
    ru: "перевод", hi: "अनुवाद", ar: "ترجمة", "zh-CN": "翻译中",
    sw: "inatafsiri", ko: "번역 중", vi: "đang dịch", tl: "isinasalin",
    ht: "ap tradwi", ja: "翻訳中", nl: "vertalen", pl: "tłumaczenie", tr: "çevriliyor",
  },

  // ── Reference pages ─────────────────────────────────────────
  "Bible Dictionary": {
    es: "Diccionario Bíblico", fr: "Dictionnaire Biblique", pt: "Dicionário Bíblico",
    de: "Bibellexikon", it: "Dizionario Biblico", ru: "Библейский словарь",
    hi: "बाइबल शब्दकोश", ar: "قاموس الكتاب المقدس", "zh-CN": "圣经词典",
    sw: "Kamusi ya Biblia", ko: "성경 사전", vi: "Từ điển Kinh Thánh",
    tl: "Diksyunaryo ng Bibliya", ht: "Diksyonè Bib", ja: "聖書辞典",
    nl: "Bijbelwoordenboek", pl: "Słownik Biblijny", tr: "İncil Sözlüğü",
  },
  "Strong's Concordance": {
    es: "Concordancia de Strong", fr: "Concordance de Strong", pt: "Concordância de Strong",
    de: "Strongs Konkordanz", it: "Concordanza di Strong", ru: "Симфония Стронга",
    hi: "स्ट्रॉन्ग की समवर्ती", ar: "موافقة سترونغ", "zh-CN": "斯特朗经文汇编",
    sw: "Konkordansi ya Strong", ko: "스트롱 성구 사전", vi: "Concordance Strong",
    tl: "Strong's Concordance", ht: "Konkòdans Strong", ja: "ストロング・コンコーダンス",
    nl: "Strong's Concordantie", pl: "Konkordancja Stronga", tr: "Strong Konkordansı",
  },
  "Bible Precepts": {
    es: "Preceptos Bíblicos", fr: "Préceptes Bibliques", pt: "Preceitos Bíblicos",
    de: "Biblische Gebote", it: "Precetti Biblici", ru: "Библейские заповеди",
    hi: "बाइबल के निर्देश", ar: "وصايا الكتاب المقدس", "zh-CN": "圣经诫命",
    sw: "Maagizo ya Biblia", ko: "성경 계명", vi: "Giáo lý Kinh Thánh",
    tl: "Mga Utos ng Bibliya", ht: "Prensip Bib la", ja: "聖書の教え",
    nl: "Bijbelse Geboden", pl: "Przykazania Biblijne", tr: "İncil Emirleri",
  },
  "The Apocrypha": {
    es: "Los Libros Apócrifos", fr: "Les Apocryphes", pt: "Os Apócrifos",
    de: "Die Apokryphen", it: "Gli Apocrifi", ru: "Апокрифы",
    hi: "अपोक्रिफा", ar: "الأبوكريفا", "zh-CN": "次经",
    sw: "Vitabu vya Siri", ko: "외경", vi: "Ngoại thư",
    tl: "Apokripa", ht: "Apokrif yo", ja: "外典",
    nl: "De Apocriefen", pl: "Apokryfy", tr: "Apokrif Kitaplar",
  },
  "The Hidden Books": {
    es: "Los Libros Ocultos", fr: "Les Livres Cachés", pt: "Os Livros Ocultos",
    de: "Die Verborgenen Bücher", it: "I Libri Nascosti", ru: "Тайные книги",
    hi: "छिपी हुई पुस्तकें", ar: "الكتب الخفية", "zh-CN": "隐秘之书",
    sw: "Vitabu vya Siri", ko: "숨겨진 책들", vi: "Những cuốn sách bí ẩn",
    tl: "Mga Nakatagong Aklat", ht: "Liv Kache yo", ja: "隠された書物",
    nl: "De Verborgen Boeken", pl: "Ukryte Księgi", tr: "Gizli Kitaplar",
  },
  "entries": {
    es: "entradas", fr: "entrées", pt: "entradas", de: "Einträge", it: "voci",
    ru: "записей", hi: "प्रविष्टियाँ", ar: "إدخالات", "zh-CN": "条目",
    sw: "ingia", ko: "항목", vi: "mục", tl: "mga entri",
    ht: "antre", ja: "項目", nl: "items", pl: "wpisy", tr: "girişler",
  },
  "precepts": {
    es: "preceptos", fr: "préceptes", pt: "preceitos", de: "Gebote", it: "precetti",
    ru: "заповеди", hi: "निर्देश", ar: "وصايا", "zh-CN": "诫命",
    sw: "maagizo", ko: "계명", vi: "giáo lý", tl: "mga utos",
    ht: "prensip", ja: "教え", nl: "geboden", pl: "przykazania", tr: "emirler",
  },
  "Positive": {
    es: "Positivo", fr: "Positif", pt: "Positivo", de: "Positiv", it: "Positivo",
    ru: "Позитивный", hi: "सकारात्मक", ar: "إيجابي", "zh-CN": "积极",
    sw: "Chanya", ko: "긍정적", vi: "Tích cực", tl: "Positibo",
    ht: "Pozitif", ja: "肯定的", nl: "Positief", pl: "Pozytywny", tr: "Olumlu",
  },
  "Negative": {
    es: "Negativo", fr: "Négatif", pt: "Negativo", de: "Negativ", it: "Negativo",
    ru: "Негативный", hi: "नकारात्मक", ar: "سلبي", "zh-CN": "消极",
    sw: "Hasi", ko: "부정적", vi: "Tiêu cực", tl: "Negatibo",
    ht: "Negatif", ja: "否定的", nl: "Negatief", pl: "Negatywny", tr: "Olumsuz",
  },
  "All": {
    es: "Todos", fr: "Tous", pt: "Todos", de: "Alle", it: "Tutti",
    ru: "Все", hi: "सभी", ar: "الكل", "zh-CN": "全部",
    sw: "Zote", ko: "모두", vi: "Tất cả", tl: "Lahat",
    ht: "Tout", ja: "すべて", nl: "Alle", pl: "Wszystkie", tr: "Tümü",
  },
  "Torah": {
    es: "Torá", fr: "Torah", pt: "Torá", de: "Tora", it: "Torah",
    ru: "Тора", hi: "तोरा", ar: "التوراة", "zh-CN": "妥拉",
    sw: "Torati", ko: "토라", vi: "Torah", tl: "Tora",
    ht: "Tora", ja: "トーラー", nl: "Thora", pl: "Tora", tr: "Tora",
  },
  "Old Testament": {
    es: "Antiguo Testamento", fr: "Ancien Testament", pt: "Antigo Testamento",
    de: "Altes Testament", it: "Antico Testamento", ru: "Ветхий Завет",
    hi: "पुराना नियम", ar: "العهد القديم", "zh-CN": "旧约",
    sw: "Agano la Kale", ko: "구약", vi: "Cựu Ước",
    tl: "Lumang Tipan", ht: "Ansyen Testaman", ja: "旧約聖書",
    nl: "Oude Testament", pl: "Stary Testament", tr: "Eski Ahit",
  },
  "New Testament": {
    es: "Nuevo Testamento", fr: "Nouveau Testament", pt: "Novo Testamento",
    de: "Neues Testament", it: "Nuovo Testamento", ru: "Новый Завет",
    hi: "नया नियम", ar: "العهد الجديد", "zh-CN": "新约",
    sw: "Agano Jipya", ko: "신약", vi: "Tân Ước",
    tl: "Bagong Tipan", ht: "Nouvo Testaman", ja: "新約聖書",
    nl: "Nieuwe Testament", pl: "Nowy Testament", tr: "Yeni Ahit",
  },
  "Whole Bible · 1611 KJV": {
    es: "Biblia Completa · 1611 KJV", fr: "Bible Entière · 1611 KJV",
    pt: "Bíblia Inteira · 1611 KJV", de: "Ganze Bibel · 1611 KJV",
    it: "Bibbia Intera · 1611 KJV", ru: "Полная Библия · 1611 KJV",
    hi: "पूरी बाइबल · 1611 KJV", ar: "الكتاب المقدس الكامل · 1611 KJV",
    "zh-CN": "完整圣经 · 1611 KJV", sw: "Biblia Yote · 1611 KJV",
    ko: "성경 전체 · 1611 KJV", vi: "Toàn bộ Kinh Thánh · 1611 KJV",
    tl: "Buong Bibliya · 1611 KJV", ht: "Tout Bib la · 1611 KJV",
    ja: "聖書全体 · 1611 KJV", nl: "Hele Bijbel · 1611 KJV",
    pl: "Cała Biblia · 1611 KJV", tr: "Tüm İncil · 1611 KJV",
  },
  "KJV 1611 · Apocrypha": {
    es: "KJV 1611 · Apócrifos", fr: "KJV 1611 · Apocryphes",
    pt: "KJV 1611 · Apócrifos", de: "KJV 1611 · Apokryphen",
    it: "KJV 1611 · Apocrifi", ru: "KJV 1611 · Апокрифы",
    hi: "KJV 1611 · अपोक्रिफा", ar: "KJV 1611 · الأبوكريفا",
    "zh-CN": "KJV 1611 · 次经", sw: "KJV 1611 · Vitabu vya Siri",
    ko: "KJV 1611 · 외경", vi: "KJV 1611 · Ngoại thư",
    tl: "KJV 1611 · Apokripa", ht: "KJV 1611 · Apokrif",
    ja: "KJV 1611 · 外典", nl: "KJV 1611 · Apocriefen",
    pl: "KJV 1611 · Apokryfy", tr: "KJV 1611 · Apokrif",
  },
  "Read aloud": {
    es: "Leer en voz alta", fr: "Lire à haute voix", pt: "Ler em voz alta",
    de: "Vorlesen", it: "Leggi ad alta voce", ru: "Прочитать вслух",
    hi: "ज़ोर से पढ़ें", ar: "اقرأ بصوت عالٍ", "zh-CN": "朗读",
    sw: "Soma kwa sauti", ko: "소리내어 읽기", vi: "Đọc to",
    tl: "Basahin nang malakas", ht: "Li awotvwa", ja: "音読する",
    nl: "Hardop voorlezen", pl: "Czytaj na głos", tr: "Sesli oku",
  },
  "Translating…": {
    es: "Traduciendo…", fr: "Traduction…", pt: "Traduzindo…",
    de: "Übersetzen…", it: "Traduzione…", ru: "Перевод…",
    hi: "अनुवाद हो रहा है…", ar: "جار الترجمة…", "zh-CN": "翻译中…",
    sw: "Inatafsiri…", ko: "번역 중…", vi: "Đang dịch…",
    tl: "Isinasalin…", ht: "Ap tradwi…", ja: "翻訳中…",
    nl: "Vertalen…", pl: "Tłumaczenie…", tr: "Çeviriliyor…",
  },
  "No entries found.": {
    es: "No se encontraron entradas.", fr: "Aucune entrée trouvée.",
    pt: "Nenhuma entrada encontrada.", de: "Keine Einträge gefunden.",
    it: "Nessuna voce trovata.", ru: "Записей не найдено.",
    hi: "कोई प्रविष्टि नहीं मिली।", ar: "لم يتم العثور على إدخالات.",
    "zh-CN": "未找到条目。", sw: "Hakuna ingizo lililopatikana.",
    ko: "항목이 없습니다.", vi: "Không tìm thấy mục nào.",
    tl: "Walang nakitang entri.", ht: "Pa gen antre yo jwenn.",
    ja: "項目が見つかりませんでした。", nl: "Geen items gevonden.",
    pl: "Nie znaleziono wpisów.", tr: "Giriş bulunamadı.",
  },
  "No books match your search.": {
    es: "Ningún libro coincide con tu búsqueda.",
    fr: "Aucun livre ne correspond à votre recherche.",
    pt: "Nenhum livro corresponde à sua pesquisa.",
    de: "Keine Bücher entsprechen Ihrer Suche.",
    it: "Nessun libro corrisponde alla tua ricerca.",
    ru: "Ни одна книга не соответствует вашему запросу.",
    hi: "आपकी खोज से कोई पुस्तक मेल नहीं खाती।",
    ar: "لا توجد كتب تطابق بحثك.",
    "zh-CN": "没有匹配您搜索的书籍。",
    sw: "Hakuna vitabu vinavyolingana na utafutaji wako.",
    ko: "검색과 일치하는 책이 없습니다.",
    vi: "Không có sách nào khớp với tìm kiếm của bạn.",
    tl: "Walang aklat na tumutugma sa iyong paghahanap.",
    ht: "Pa gen liv ki koresponn ak rechèch ou.",
    ja: "検索に一致する本はありません。",
    nl: "Geen boeken komen overeen met uw zoekopdracht.",
    pl: "Żadne książki nie pasują do twojego wyszukiwania.",
    tr: "Aramanızla eşleşen kitap yok.",
  },
  "All apocryphal books": {
    es: "Todos los libros apócrifos", fr: "Tous les livres apocryphes",
    pt: "Todos os livros apócrifos", de: "Alle apokryphen Bücher",
    it: "Tutti i libri apocrifi", ru: "Все апокрифические книги",
    hi: "सभी अपोक्रिफा पुस्तकें", ar: "جميع الكتب الأبوكريفية",
    "zh-CN": "所有次经书卷", sw: "Vitabu vyote vya siri",
    ko: "모든 외경", vi: "Tất cả các sách ngoại thư",
    tl: "Lahat ng apokripal na aklat", ht: "Tout liv apokrif",
    ja: "すべての外典", nl: "Alle apocriefe boeken",
    pl: "Wszystkie księgi apokryficzne", tr: "Tüm apokrif kitaplar",
  },
};

// Hydrate the singleton localStorage cache used by
// useCaptionTranslation. We write directly to the same key so the
// hook picks the entries up on its next render — no API change,
// no race condition. Idempotent: safe to call on every boot.
//
// We merge the AUTO-GENERATED manifest (generated nightly by
// `scripts/generate_bundled_translations.py` against the Claude
// Haiku translation backend) on TOP of the hand-curated manifest
// above. Hand-curated entries win on conflict so we can override
// machine output if it's ever wrong.
import GENERATED from "./bundledTranslations.generated";

const LS_KEY = "hw:i18n:cache:v2";

export function hydrateBundledTranslations() {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    const existing = raw ? JSON.parse(raw) : {};
    let added = 0;
    // Generated bundle FIRST so the hand-curated MANIFEST below can
    // overwrite anything that's wrong.
    for (const [src, langs] of Object.entries(GENERATED || {})) {
      for (const [lang, tx] of Object.entries(langs)) {
        const k = `${lang}::${src}`;
        if (!existing[k]) {
          existing[k] = tx;
          added++;
        }
      }
    }
    for (const [src, langs] of Object.entries(MANIFEST)) {
      for (const [lang, tx] of Object.entries(langs)) {
        const k = `${lang}::${src}`;
        if (!existing[k]) {
          existing[k] = tx;
          added++;
        }
      }
    }
    if (added > 0) {
      window.localStorage.setItem(LS_KEY, JSON.stringify(existing));
    }
  } catch (_) {
    // Cache hydration is a best-effort optimization; never crash
    // the app over a corrupted localStorage.
  }
}

export default MANIFEST;
